import { computed, isRef, ref, watch, type Ref } from "vue";
import { parsePhoneNumberWithError, type CountryCode } from "libphonenumber-js";
import { phoneMatchesCandidates, phoneSearchCandidates, type PhoneSearchCandidates } from "@/helpers/directoryQuery";

export type RecipientChannel = "email" | "phone";

export interface ContactRecipient {
	channel: RecipientChannel;
	value: string;
	displayValue: string;
	source: "contact" | "manual";
	contactId?: string;
	contactName?: string;
	/**
	 * Whether this recipient passed channel validation. Omitted (treated as valid) for
	 * contact-sourced recipients and successfully-canonicalized manual entries. Set to `false`
	 * for manual entries the user typed that failed validation — those are preserved as a red
	 * "invalid" pill (LSP-16629) so the entry is visible and fixable rather than silently dropped.
	 */
	valid?: boolean;
}

interface CustomerRecord {
	id: string;
	firstname?: string | null;
	lastname?: string | null;
	companyName?: string | null;
	EmailAddresses?: Array<{ id?: string; emailAddress: string }>;
	PhoneNumbers?: Array<{ id?: string; phoneNumber: string; phonenumberraw?: string | null }>;
}

export interface UseContactRecipientSearchOptions {
	channel: RecipientChannel;
	customers: Ref<unknown[]>;
	selectedValues: Ref<Set<string>>;
	/**
	 * The org's default country. Accepts either a plain value (stable for the lifetime of the
	 * composable) or a Ref so the composable reacts when the caller's org data resolves. When
	 * undefined, stored phones must be in E.164 form — silently applying "US" would wrong-
	 * canonicalize non-US orgs' national-format numbers and drop them from the dropdown.
	 */
	defaultCountry?: CountryCode | Ref<CountryCode | undefined> | undefined;
	maxResults?: number;
}

export interface UseContactRecipientSearchResult {
	search: (raw: string) => void;
	results: Ref<ContactRecipient[]>;
}

export function useContactRecipientSearch(options: UseContactRecipientSearchOptions): UseContactRecipientSearchResult {
	const { channel, customers, selectedValues, defaultCountry, maxResults = 50 } = options;

	const searchTerm = ref("");

	// Resolve defaultCountry on every computed run so a Ref-valued option re-triggers the
	// expansion when the org data resolves (e.g. undefined → "GB" after /me returns). A plain
	// value resolves to itself. Uses Vue's canonical isRef (checks __v_isRef marker).
	const resolveDefaultCountry = (): CountryCode | undefined =>
		isRef(defaultCountry) ? (defaultCountry.value as CountryCode | undefined) : defaultCountry;

	// Expand every customer into their per-channel rows once. This is an O(n) pass; recompute
	// when the underlying customer list, the dedup set, OR the default country changes.
	const allRecipients = computed<ExpandedRecipient[]>(() => {
		const out: ExpandedRecipient[] = [];
		const selected = selectedValues.value;
		const country = resolveDefaultCountry();
		for (const customer of (customers.value as CustomerRecord[]) ?? []) {
			const displayName = buildDisplayName(customer);
			// Built once per customer and shared by every row they produce — see buildCustomerSearchIndex
			// for why the index spans BOTH channels.
			const searchIndex = buildCustomerSearchIndex(customer, displayName);
			if (channel === "email") {
				for (const record of customer.EmailAddresses ?? []) {
					const canonical = canonicalizeEmail(record?.emailAddress);
					if (!canonical || selected.has(canonical)) continue;
					out.push(
						expand(
							{
								channel: "email",
								value: canonical,
								displayValue: displayName ? `${displayName} — ${canonical}` : canonical,
								source: "contact",
								contactId: customer.id,
								contactName: displayName || undefined
							},
							searchIndex
						)
					);
				}
			} else {
				for (const record of customer.PhoneNumbers ?? []) {
					// phonenumberraw is the user's literal input (often national-format with no country
					// code); phoneNumber is the canonical international form contact-api stores (always
					// carries a leading +, so it parses without a default country). Canonicalize the raw
					// first to honor the user's intended number when the org's default country is known,
					// then fall back to the always-parseable phoneNumber so a contact never silently
					// drops out of the phone picker when the org has no defaultCountryCode (LSP-16627).
					const canonical = canonicalizePhone(record?.phonenumberraw, country) ?? canonicalizePhone(record?.phoneNumber, country);
					if (!canonical || selected.has(canonical)) continue;
					out.push(
						expand(
							{
								channel: "phone",
								value: canonical,
								displayValue: displayName
									? `${displayName} — ${formatPhoneForDisplay(canonical)}`
									: formatPhoneForDisplay(canonical),
								source: "contact",
								contactId: customer.id,
								contactName: displayName || undefined
							},
							searchIndex
						)
					);
				}
			}
		}
		return out;
	});

	const results = ref<ContactRecipient[]>([]) as Ref<ContactRecipient[]>;

	function filterResults(rawTerm: string): ContactRecipient[] {
		const trimmed = rawTerm.trim();
		if (!trimmed) return allRecipients.value.slice(0, maxResults).map((entry) => entry.recipient);

		// Computed once per query, not per row.
		const query = buildRecipientQuery(trimmed);

		const out: ContactRecipient[] = [];
		for (const entry of allRecipients.value) {
			if (matchesRecipient(entry, query)) {
				out.push(entry.recipient);
				if (out.length >= maxResults) break;
			}
		}
		return out;
	}

	function recompute(): void {
		results.value = filterResults(searchTerm.value);
	}

	watch(allRecipients, recompute, { immediate: true });

	const search = (raw: string): void => {
		searchTerm.value = raw ?? "";
		recompute();
	};

	return { search, results };
}

interface RecipientQuery {
	/** Lowercased whitespace-separated words — each one must match at least one field. */
	readonly words: readonly string[];
	/** Digit/literal renderings of a phone-shaped query, or null when it isn't one. */
	readonly phoneCandidates: PhoneSearchCandidates | null;
}

/** What one dropdown row is matched by, kept beside the row rather than inside it. */
interface ExpandedRecipient {
	readonly recipient: ContactRecipient;
	/** Lowercased text of every identifier the contact has — see buildCustomerSearchIndex. */
	readonly haystack: string;
	/** Digit run of every phone the contact has, for phone-shaped queries. */
	readonly phoneDigits: readonly string[];
}

/**
 * The text and digits a contact can be FOUND by, spanning BOTH channels.
 *
 * `api.searchCustomers` ORs its per-word predicate across firstname, lastname,
 * `EmailAddresses.emailAddress` AND `PhoneNumbers.phonenumberraw` — it does not know or care which
 * picker asked. So a row the server returned because the query matched the contact's PHONE arrives
 * in the email picker, where the row itself carries only an address: testing the row's own value
 * discards the contact the user just searched for, which is the LSP-20700 dead end reached from a
 * different direction. Matching against every identifier keeps client and server answering the
 * same question. The row still RENDERS its own channel's value — only what it is findable by widens.
 */
function buildCustomerSearchIndex(customer: CustomerRecord, displayName: string): { haystack: string; phoneDigits: string[] } {
	const parts: string[] = [];
	if (displayName) parts.push(displayName);
	if (customer.firstname) parts.push(customer.firstname);
	if (customer.lastname) parts.push(customer.lastname);
	if (customer.companyName) parts.push(customer.companyName);
	for (const record of customer.EmailAddresses ?? []) {
		if (record?.emailAddress) parts.push(record.emailAddress);
	}
	const phoneDigits: string[] = [];
	for (const record of customer.PhoneNumbers ?? []) {
		// Both stored forms: phonenumberraw holds contiguous digits, phoneNumber holds
		// formatInternational() WITH spaces. A literal query can match either rendering.
		for (const stored of [record?.phonenumberraw, record?.phoneNumber]) {
			if (!stored) continue;
			parts.push(stored);
			const digits = stored.replace(/\D/g, "");
			if (digits) phoneDigits.push(digits);
		}
	}
	return { haystack: parts.join(" ").toLowerCase(), phoneDigits };
}

/** Pair a row with its contact's index, plus the row's own canonical value and label. */
function expand(recipient: ContactRecipient, index: { haystack: string; phoneDigits: string[] }): ExpandedRecipient {
	const phoneDigits = index.phoneDigits.slice();
	if (recipient.channel === "phone") {
		// The canonical E.164 this row renders can differ from either stored form (the composable
		// re-parses it against the org's country), so it is indexed too.
		const digits = recipient.value.replace(/\D/g, "");
		if (digits) phoneDigits.push(digits);
	}
	return {
		recipient,
		haystack: `${index.haystack} ${recipient.value} ${recipient.displayValue}`.toLowerCase(),
		phoneDigits
	};
}

function buildRecipientQuery(trimmedTerm: string): RecipientQuery {
	return {
		words: trimmedTerm
			.toLowerCase()
			.split(/\s+/)
			.filter((word) => word !== ""),
		phoneCandidates: phoneSearchCandidates(trimmedTerm)
	};
}

/**
 * Mirrors `api.searchCustomers`'s predicate — per word, every word matching at least one column
 * (its `$and` of `$or`s), plus a phone-shaped query matched as ONE run of digits.
 *
 * This used to be a whole-query substring test, which made the client STRICTER than the server it
 * filters the results of. That is the defect LSP-15692 already fixed on the project-share side
 * (`ShareInviteField.directoryHits`): "jo smith" → "Jonathan Smith" and cross-field matches
 * ("jo x.com" → name + email) are hits the server returns and a whole-query test dropped before
 * they could render. It matters more now that these results can come FROM the server (LSP-20700):
 * a filter stricter than the query that fetched the rows silently discards the contact the user
 * just searched for, which is the same dead end from a different direction.
 */
function matchesRecipient(entry: ExpandedRecipient, query: RecipientQuery): boolean {
	// Phone-specific, and tested FIRST because a number must never be split into per-word terms:
	// "+1 212 567 8901" would become four required substrings, none of which a stored "+12125678901"
	// contains. Digit-run matching is also what lets "(212) 567-8901" find it. Tested against EVERY
	// phone the contact has, not just the one this row renders, so a phone query finds them in the
	// email picker too — exactly as the server predicate does.
	if (query.phoneCandidates) {
		for (const digits of entry.phoneDigits) {
			if (phoneMatchesCandidates(digits, query.phoneCandidates)) return true;
		}
	}

	// A query too short to be phone-shaped (fewer digits than MIN_PHONE_DIGITS) still reaches here
	// and matches a number by plain substring, which is how a partial "212" narrows the list.
	return query.words.length > 0 && query.words.every((word) => entry.haystack.includes(word));
}

function buildDisplayName(customer: CustomerRecord): string {
	const full = [customer.firstname, customer.lastname].filter(Boolean).join(" ").trim();
	return full || customer.companyName || "";
}

function canonicalizeEmail(value: string | undefined): string | null {
	if (!value) return null;
	const trimmed = value.trim().toLowerCase();
	return trimmed || null;
}

function canonicalizePhone(value: string | null | undefined, defaultCountry: CountryCode | undefined): string | null {
	if (!value) return null;
	try {
		// When defaultCountry is undefined (org not yet loaded or no default set), parse strictly
		// — value must already be in E.164 form. Silently falling back to "US" here would
		// wrong-canonicalize stored national-format phones for non-US orgs.
		const parsed = defaultCountry
			? parsePhoneNumberWithError(value, { defaultCountry, extract: false })
			: parsePhoneNumberWithError(value, { extract: false });
		if (!parsed.isValid()) return null;
		return parsed.number;
	} catch {
		return null;
	}
}

/**
 * Format an E.164 phone number as a human-readable international string
 * (e.g. "+12125678901" → "+1 212 567 8901"). Falls back to the input unchanged
 * if the number can't be parsed. Exported so RecipientPicker can render chips
 * and dropdown subtitles with the same formatting the composable uses.
 */
export function formatPhoneForDisplay(e164: string): string {
	try {
		return parsePhoneNumberWithError(e164).formatInternational();
	} catch {
		return e164;
	}
}
